import { useEffect, useState, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  GraduationCap, Plus, Trash2, Pencil, RotateCcw, Eye, Video, Layers, Compass, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import TrainingPlayer from './TrainingPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'staff', label: 'Staff (Agents · Supervisors · Crew Leaders)' },
  { value: 'property_manager', label: 'Property Managers' },
  { value: 'tenant', label: 'Tenants / Residents' },
];

const TYPE_ICON = { slideshow: Layers, video: Video, tour: Compass };

/**
 * Admin TrainingHub: manage modules, settings, completion roster.
 */
export default function TrainingHub({ dataTestId = 'training-hub' }) {
  const [modules, setModules] = useState([]);
  const [roster, setRoster] = useState([]);
  const [settings, setSettings] = useState([]);
  const [previewModule, setPreviewModule] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [modsRes, rosRes, setsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/training/modules`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/training/roster`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/training/settings`, { credentials: 'include' }),
      ]);
      if (modsRes.ok) setModules((await modsRes.json()).modules || []);
      if (rosRes.ok) setRoster((await rosRes.json()).roster || []);
      if (setsRes.ok) setSettings((await setsRes.json()).settings || []);
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = (role) => {
    setEditing({
      role,
      title: '',
      description: '',
      type: 'slideshow',
      video_url: '',
      slides: [{ title: '', caption: '', icon: 'play', accent: '#C9A962', duration_ms: 4000 }],
      required: false,
      order: 100,
    });
    setEditorOpen(true);
  };

  const openEdit = (m) => {
    setEditing({ ...m });
    setEditorOpen(true);
  };

  const saveModule = async () => {
    const isNew = !editing.module_id;
    const url = isNew
      ? `${BACKEND_URL}/api/training/modules`
      : `${BACKEND_URL}/api/training/modules/${editing.module_id}`;
    try {
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editing),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Save failed');
        return;
      }
      toast.success(isNew ? 'Module created' : 'Module updated');
      setEditorOpen(false);
      setEditing(null);
      fetchAll();
    } catch (_e) {
      toast.error('Save failed');
    }
  };

  const removeModule = async (m) => {
    if (m.is_default) {
      toast.error('Default modules cannot be deleted — edit instead.');
      return;
    }
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/modules/${m.module_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        toast.error('Delete failed');
        return;
      }
      toast.success('Deleted');
      fetchAll();
    } catch (_e) { /* noop */ }
  };

  const retrigger = async (userId, moduleId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/settings/retrigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, module_id: moduleId }),
      });
      if (res.ok) {
        toast.success('Re-triggered for user');
        fetchAll();
      }
    } catch (_e) { /* noop */ }
  };

  const downloadUserCertificate = async (user) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/certificate/${user.user_id}`, { credentials: 'include' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'User not eligible yet');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NECS_Certificate_${(user.name || 'User').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Certificate downloaded for ${user.name}`);
    } catch (_e) { toast.error('Failed'); }
  };

  const upsertSetting = async (payload) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/training/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Saved');
        fetchAll();
      }
    } catch (_e) { /* noop */ }
  };

  return (
    <div className="space-y-4" data-testid={dataTestId}>
      <Card className="bg-zinc-900 border-zinc-700 p-5 flex items-center gap-3">
        <GraduationCap className="h-6 w-6 text-[#C9A962]" />
        <div>
          <h3 className="text-white text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            TRAINING HUB
          </h3>
          <p className="text-xs text-zinc-400">
            Manage in-app training for Staff, Property Managers, and Tenants. Track completion, gate first login, re-trigger anytime.
          </p>
        </div>
      </Card>

      <Tabs defaultValue="modules">
        <TabsList className="bg-zinc-900">
          <TabsTrigger value="modules" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black" data-testid="training-subtab-modules">Modules</TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black" data-testid="training-subtab-gating">Gating</TabsTrigger>
          <TabsTrigger value="roster" className="data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black" data-testid="training-subtab-completions">Completions</TabsTrigger>
        </TabsList>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          {ROLES.map((r) => (
            <Card key={r.value} className="bg-zinc-900 border-zinc-700 p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h4 className="text-white font-semibold">{r.label}</h4>
                <Button
                  className="font-bold"
                  size="sm"
                  style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                  onClick={() => openCreate(r.value)}
                  data-testid={`training-add-${r.value}`}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Module
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {modules.filter((m) => m.role === r.value).length === 0 && (
                  <p className="text-zinc-500 text-sm">No modules yet.</p>
                )}
                {modules.filter((m) => m.role === r.value).map((m) => {
                  const TIcon = TYPE_ICON[m.type] || Layers;
                  return (
                    <div
                      key={m.module_id}
                      className="rounded-lg p-4 border border-white/10 bg-[#222]"
                      data-testid={`training-module-${m.module_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <TIcon className="h-4 w-4 text-[#C9A962]" />
                            <p className="text-white font-semibold leading-tight">{m.title}</p>
                            {m.required && <Badge className="bg-[#C9A962] text-[#2D2D2D] text-[10px]">REQUIRED</Badge>}
                            {m.is_default && <Badge className="bg-zinc-700 text-zinc-200 text-[10px]">DEFAULT</Badge>}
                          </div>
                          {m.description && <p className="text-xs text-zinc-400 mt-1">{m.description}</p>}
                          <p className="text-[11px] text-zinc-500 mt-1">
                            {m.type === 'video' ? 'Video' : `${m.slides?.length || 0} slides`}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#C9A962] text-[#C9A962]"
                          onClick={() => { setPreviewModule(m); setPreviewOpen(true); }}
                          data-testid={`training-preview-${m.module_id}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-600 text-zinc-300"
                          onClick={() => openEdit(m)}
                          data-testid={`training-edit-${m.module_id}`}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                        {!m.is_default && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400"
                            onClick={() => removeModule(m)}
                            data-testid={`training-delete-${m.module_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card className="bg-zinc-900 border-zinc-700 p-5">
            <h4 className="text-white font-semibold mb-3">Gating by Role</h4>
            <p className="text-xs text-zinc-400 mb-4">
              Toggle whether required modules block first login / first clock-in for a whole role.
            </p>
            <div className="space-y-3">
              {ROLES.map((r) => {
                const setting = settings.find((s) => s.role === r.value && !s.user_id && !s.property_id) || {};
                return (
                  <div key={r.value} className="flex items-center justify-between bg-[#222] rounded-lg p-4 flex-wrap gap-3" data-testid={`gating-row-${r.value}`}>
                    <div>
                      <p className="text-white font-semibold">{r.label}</p>
                      <p className="text-[11px] text-zinc-500">Default: enabled, gating off</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <ToggleRow
                        label="Enabled"
                        value={setting.enabled !== false}
                        onChange={(v) => upsertSetting({ role: r.value, enabled: v, require_completion: !!setting.require_completion })}
                        testId={`gating-enabled-${r.value}`}
                      />
                      <ToggleRow
                        label="Require completion"
                        value={!!setting.require_completion}
                        onChange={(v) => upsertSetting({ role: r.value, enabled: setting.enabled !== false, require_completion: v })}
                        testId={`gating-require-${r.value}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="bg-zinc-900 border-zinc-700 p-5">
            <h4 className="text-white font-semibold mb-1">Per-User / Per-Property Override</h4>
            <p className="text-xs text-zinc-400 mb-3">
              Use the Completions tab to disable training or re-trigger it for individual users. Property-level overrides via API.
            </p>
            <p className="text-xs text-zinc-500">
              {settings.filter((s) => s.user_id || s.property_id).length} active override(s).
            </p>
          </Card>
        </TabsContent>

        {/* Completions Tab */}
        <TabsContent value="roster" className="space-y-3">
          <Card className="bg-zinc-900 border-zinc-700 p-5">
            <h4 className="text-white font-semibold mb-3">Completion Roster ({roster.length})</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 border-b border-white/10">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Role</th>
                    <th className="py-2 pr-3">Badge / Property</th>
                    <th className="py-2 pr-3">Completed</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.map((u) => (
                    <tr key={u.user_id} className="border-b border-white/5" data-testid={`roster-row-${u.user_id}`}>
                      <td className="py-2 pr-3 text-white">{u.name}</td>
                      <td className="py-2 pr-3 text-zinc-400 capitalize">{u.role?.replace('_', ' ')}</td>
                      <td className="py-2 pr-3 text-zinc-400">{u.badge_number || u.property_name || '—'}</td>
                      <td className="py-2 pr-3">
                        <Badge className={u.completed_count > 0 ? 'bg-green-700 text-white' : 'bg-zinc-700 text-zinc-200'}>
                          {u.completed_count} / {u.started_count || u.completed_count}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#C9A962] text-[#C9A962]"
                            onClick={() => downloadUserCertificate(u)}
                            data-testid={`certificate-${u.user_id}`}
                          >
                            <Award className="h-3.5 w-3.5 mr-1" /> Cert
                          </Button>
                          {u.completions?.filter((c) => c.completed_at).length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-500/50 text-amber-400"
                              onClick={() => {
                                const c = u.completions.find((x) => x.completed_at);
                                if (c) retrigger(u.user_id, c.module_id);
                              }}
                              data-testid={`retrigger-${u.user_id}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Re-trigger
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {roster.length === 0 && (
                    <tr><td colSpan={5} className="py-4 text-zinc-500 text-center">No users yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview */}
      <TrainingPlayer module={previewModule} open={previewOpen} onOpenChange={setPreviewOpen} />

      {/* Editor */}
      <ModuleEditor open={editorOpen} setOpen={setEditorOpen} value={editing} onChange={setEditing} onSave={saveModule} />
    </div>
  );
}

function ToggleRow({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm text-zinc-300" data-testid={testId}>
      <Switch checked={value} onCheckedChange={onChange} />
      {label}
    </label>
  );
}

function ModuleEditor({ open, setOpen, value, onChange, onSave }) {
  if (!value) return null;
  const updateSlide = (idx, key, val) => {
    const slides = [...(value.slides || [])];
    slides[idx] = { ...slides[idx], [key]: val };
    onChange({ ...value, slides });
  };
  const addSlide = () => onChange({ ...value, slides: [...(value.slides || []), { title: '', caption: '', icon: 'play', accent: '#C9A962', duration_ms: 4000 }] });
  const removeSlide = (idx) => onChange({ ...value, slides: (value.slides || []).filter((_, i) => i !== idx) });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl bg-[#2D2D2D] text-white border-[#C9A962] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#C9A962]">
            {value.module_id ? 'Edit Module' : 'New Training Module'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Role</Label>
              <Select value={value.role} onValueChange={(v) => onChange({ ...value, role: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="editor-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Type</Label>
              <Select value={value.type} onValueChange={(v) => onChange({ ...value, type: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="editor-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slideshow">Slideshow</SelectItem>
                  <SelectItem value="video">Video (URL)</SelectItem>
                  <SelectItem value="tour">Interactive Tour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">Title</Label>
            <Input value={value.title} onChange={(e) => onChange({ ...value, title: e.target.value })}
                   className="bg-zinc-800 border-zinc-700 text-white" data-testid="editor-title" />
          </div>
          <div>
            <Label className="text-zinc-300">Description</Label>
            <Textarea value={value.description || ''} onChange={(e) => onChange({ ...value, description: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white" rows={2} />
          </div>
          <div className="flex items-center gap-4">
            <ToggleRow label="Required" value={!!value.required} onChange={(v) => onChange({ ...value, required: v })} testId="editor-required" />
            <div>
              <Label className="text-zinc-300 text-xs">Order</Label>
              <Input type="number" value={value.order ?? 100} onChange={(e) => onChange({ ...value, order: parseInt(e.target.value || 100, 10) })}
                     className="bg-zinc-800 border-zinc-700 text-white w-24" />
            </div>
          </div>

          {value.type === 'video' && (
            <div>
              <Label className="text-zinc-300">Video URL (YouTube, Vimeo, or .mp4)</Label>
              <Input value={value.video_url || ''} onChange={(e) => onChange({ ...value, video_url: e.target.value })}
                     placeholder="https://www.youtube.com/watch?v=..."
                     className="bg-zinc-800 border-zinc-700 text-white" data-testid="editor-video-url" />
            </div>
          )}

          {value.type !== 'video' && (
            <div className="space-y-3 mt-1">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Slides</Label>
                <Button size="sm" onClick={addSlide} className="font-bold" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} data-testid="editor-add-slide">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Slide
                </Button>
              </div>
              {(value.slides || []).map((s, idx) => (
                <div key={idx} className="rounded-lg border border-white/10 p-3 bg-[#222] space-y-2" data-testid={`editor-slide-${idx}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-400 uppercase tracking-widest">Slide {idx + 1}</p>
                    <Button size="sm" variant="ghost" onClick={() => removeSlide(idx)} className="text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Input value={s.title} onChange={(e) => updateSlide(idx, 'title', e.target.value)} placeholder="Title"
                         className="bg-zinc-800 border-zinc-700 text-white" />
                  <Textarea value={s.caption} onChange={(e) => updateSlide(idx, 'caption', e.target.value)} placeholder="Caption"
                            className="bg-zinc-800 border-zinc-700 text-white" rows={2} />
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={s.icon || ''} onChange={(e) => updateSlide(idx, 'icon', e.target.value)} placeholder="lucide icon (e.g. qr-code)"
                           className="bg-zinc-800 border-zinc-700 text-white text-xs" />
                    <Input value={s.accent || '#C9A962'} onChange={(e) => updateSlide(idx, 'accent', e.target.value)} placeholder="#C9A962"
                           className="bg-zinc-800 border-zinc-700 text-white text-xs" />
                    <Input type="number" value={s.duration_ms || 4000} onChange={(e) => updateSlide(idx, 'duration_ms', parseInt(e.target.value || 4000, 10))} placeholder="duration ms"
                           className="bg-zinc-800 border-zinc-700 text-white text-xs" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="border-zinc-600 text-zinc-300" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="font-bold" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} onClick={onSave} data-testid="editor-save">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
